import React, { useContext } from 'react';
import _filter from 'lodash/filter';
import { AppContext } from 'commons-ui';
import { Button, KIND, SIZE } from 'baseui/button';
import { FIELD_ERROR_LABEL, MESSAGES } from '../constants';
import { DpdpaField, DpdpaGroup, DpdpaSource } from '../types';
import useStyles from '../useStyles';
import { formatDate, isStale } from '../utils/format';
import Badge from './common/Badge';
import Notice from './common/Notice';

const FieldChips: React.FC<{ field: DpdpaField }> = ({ field }) => (
	<>
		{field.encrypted && !field.redacted && !field.error && <Badge tone='success' label='Decrypted' />}
		{field.redacted && <Badge tone='neutral' label='Redacted' />}
		{field.error && <Badge tone='danger' label={FIELD_ERROR_LABEL[field.error] || 'Decrypt failed'} />}
	</>
);

const FieldRow: React.FC<{ field: DpdpaField; canCopy: boolean }> = ({ field, canCopy }) => {
	const { classes } = useStyles();
	const { showAlert } = useContext(AppContext);
	const hasValue: boolean = field.value !== null && field.value !== undefined && field.value !== '';

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(field.value || '');
			showAlert(MESSAGES.COPIED);
		} catch (err) {
			showAlert(MESSAGES.COPY_FAILED, { type: 'error' });
		}
	};

	return (
		<>
			<dt className={classes.kvKey}>{field.label || field.key}</dt>
			<dd className={classes.kvValue}>
				<span className={hasValue ? classes.kvValueText : classes.kvValueEmpty}>{hasValue ? field.value : '—'}</span>
				<FieldChips field={field} />
				{hasValue && canCopy && (
					<Button
						kind={KIND.tertiary}
						size={SIZE.mini}
						onClick={handleCopy}
						overrides={{ BaseButton: { props: { 'aria-label': `Copy ${field.label || field.key}` } } }}>
						Copy
					</Button>
				)}
			</dd>
		</>
	);
};

const GroupCard: React.FC<{ group: DpdpaGroup; canCopy: boolean }> = ({ group, canCopy }) => {
	const { classes } = useStyles();
	return (
		<section className={classes.card} aria-label={group.group}>
			<h5 className={classes.groupTitle}>{group.group}</h5>
			<dl className={classes.kvGrid}>
				{group.fields.map((field) => (
					<FieldRow key={field.key} field={field} canCopy={canCopy} />
				))}
			</dl>
		</section>
	);
};

interface ISourceSectionProps {
	source: DpdpaSource;
	// erased keys: encrypted fields are unrecoverable, show only plain fields
	hideEncrypted: boolean;
	// copying values out is an unaudited export, so it needs the export permission like the export buttons
	canCopy: boolean;
}

const SourceSection: React.FC<ISourceSectionProps> = ({ source, hideEncrypted, canCopy }) => {
	const { classes } = useStyles();
	const label: string = source.label || source.table;

	const visibleGroups: DpdpaGroup[] = (source.groups || []).map((group) => {
		const fields = group.fields || [];
		return { ...group, fields: hideEncrypted ? _filter(fields, (field) => !field.encrypted) : fields };
	});
	const groups: DpdpaGroup[] = _filter(visibleGroups, (group) => group.fields.length > 0);

	const renderBody = () => {
		if (!source.rowFound) return <Notice tone='neutral'>{`No record for this user in ${label}`}</Notice>;
		if (!groups.length) {
			return (
				<Notice tone='neutral'>
					{hideEncrypted ? `No non-encrypted fields held in ${label}.` : `No fields returned from ${label}.`}
				</Notice>
			);
		}
		return (
			<div className={classes.groupGrid}>
				{groups.map((group) => (
					<GroupCard key={group.group} group={group} canCopy={canCopy} />
				))}
			</div>
		);
	};

	return (
		<section aria-label={label} className={classes.stack}>
			<div className={classes.sourceHeader}>
				<h4 className={classes.sourceTitle}>{label}</h4>
				<span className={classes.mono}>{source.table}</span>
				{source.dataAsOf && <span className={classes.muted}>{`Data as of ${formatDate(source.dataAsOf)}`}</span>}
				{source.rowFound && isStale(source.dataAsOf) && <Badge tone='warning' label='Stale' />}
			</div>
			{renderBody()}
		</section>
	);
};

export default SourceSection;
